// Database operations (Firestore)
import { db, storage, auth } from './firebase.js';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
    collection,
    doc,
    getDoc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    setDoc,
    query,
    where,
    orderBy,
    limit,
    serverTimestamp,
    increment,
} from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';

/**
 * Get user by ID
 */
export async function getUserById(userId) {
    try {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
            const data = userSnap.data();
            let displayName = data.displayName;

            // Avoid showing 'Anonymous' if we have a better name or email
            if (!displayName || displayName === 'Anonymous') {
                displayName = data.email ? data.email.split('@')[0] : 'Utilisateur Bible';
            }

            return { id: userSnap.id, ...data, displayName };
        }

        // Return fallback if user not found
        return {
            id: userId,
            displayName: 'Utilisateur Bible',
            photoURL: null,
        };
    } catch (error) {
        console.error('❌ Error getting user:', error);
        return {
            id: userId,
            displayName: 'Utilisateur Bible',
            photoURL: null,
        };
    }
}

/**
 * Create a new post
 */
export async function createPost(postData, authorId) {
    try {
        const post = {
            ...postData,
            authorId,
            createdAt: serverTimestamp(),
            likesCount: 0,
            commentsCount: 0,
            reactions: {
                heart: [],
                amen: [],
                thoughtful: [],
                encouraging: [],
                inspiring: []
            }
        };

        const docRef = await addDoc(collection(db, 'posts'), post);
        console.log('✅ Post created:', docRef.id);
        return docRef.id;
    } catch (error) {
        console.error('❌ Error creating post:', error);
        throw error;
    }
}

/**
 * Get recent posts (feed)
 */
export async function getPosts(limitCount = 20) {
    try {
        const postsRef = collection(db, 'posts');
        const q = query(postsRef, orderBy('createdAt', 'desc'), limit(limitCount));
        const querySnapshot = await getDocs(q);

        const posts = [];
        for (const docSnap of querySnapshot.docs) {
            const postData = { id: docSnap.id, ...docSnap.data() };

            // Initialize reactions if they don't exist
            if (!postData.reactions) {
                postData.reactions = {
                    heart: [],
                    amen: [],
                    thoughtful: [],
                    encouraging: [],
                    inspiring: []
                };
            }

            // Fetch author info
            postData.author = await getUserById(postData.authorId);

            posts.push(postData);
        }

        console.log(`✅ Fetched ${posts.length} posts`);
        return posts;
    } catch (error) {
        console.error('❌ Error getting posts:', error);
        throw error;
    }
}

/**
 * Get post by ID
 */
export async function getPostById(postId) {
    try {
        const postRef = doc(db, 'posts', postId);
        const postSnap = await getDoc(postRef);

        if (!postSnap.exists()) {
            throw new Error('Post not found');
        }

        const postData = { id: postSnap.id, ...postSnap.data() };

        // Fetch author info
        postData.author = await getUserById(postData.authorId);

        return postData;
    } catch (error) {
        console.error('❌ Error getting post:', error);
        throw error;
    }
}

/**
 * Update a post
 */
export async function updatePost(postId, updates) {
    try {
        const postRef = doc(db, 'posts', postId);
        await updateDoc(postRef, updates);
        console.log('✅ Post updated:', postId);
    } catch (error) {
        console.error('❌ Error updating post:', error);
        throw error;
    }
}

/**
 * Repost (share) an existing post to the feed
 */
export async function repostPost(originalPostId, authorId) {
    try {
        const repost = {
            type: 'repost',
            originalPostId,
            text: '',
            authorId,
            createdAt: serverTimestamp(),
            likesCount: 0,
            commentsCount: 0,
            sharesCount: 0,
            reactions: {
                heart: [],
                amen: [],
                thoughtful: [],
                encouraging: [],
                inspiring: []
            }
        };

        const docRef = await addDoc(collection(db, 'posts'), repost);

        // Increment sharesCount on original post
        const originalRef = doc(db, 'posts', originalPostId);
        await updateDoc(originalRef, { sharesCount: increment(1) });

        console.log('✅ Repost created:', docRef.id);
        return docRef.id;
    } catch (error) {
        console.error('❌ Error reposting:', error);
        throw error;
    }
}

/**
 * Delete a post
 */
export async function deletePost(postId) {
    try {
        const postRef = doc(db, 'posts', postId);
        await deleteDoc(postRef);
        console.log('✅ Post deleted:', postId);
    } catch (error) {
        console.error('❌ Error deleting post:', error);
        throw error;
    }
}

/**
 * Add a comment to a post
 */
export async function addComment(postId, text, authorId) {
    try {
        const comment = {
            text,
            authorId,
            createdAt: serverTimestamp(),
        };

        const commentsRef = collection(db, 'posts', postId, 'comments');
        const docRef = await addDoc(commentsRef, comment);

        // Increment comments count
        const postRef = doc(db, 'posts', postId);
        const postSnap = await getDoc(postRef);
        if (postSnap.exists()) {
            await updateDoc(postRef, {
                commentsCount: (postSnap.data().commentsCount || 0) + 1,
            });
        }

        console.log('✅ Comment added:', docRef.id);
        return docRef.id;
    } catch (error) {
        console.error('❌ Error adding comment:', error);
        throw error;
    }
}

/**
 * Get comments for a post
 */
export async function getComments(postId) {
    try {
        const commentsRef = collection(db, 'posts', postId, 'comments');
        const q = query(commentsRef, orderBy('createdAt', 'asc'));
        const querySnapshot = await getDocs(q);

        const comments = [];
        for (const docSnap of querySnapshot.docs) {
            const commentData = { id: docSnap.id, ...docSnap.data() };

            // Fetch author info
            commentData.author = await getUserById(commentData.authorId);

            comments.push(commentData);
        }

        console.log(`✅ Fetched ${comments.length} comments`);
        return comments;
    } catch (error) {
        console.error('❌ Error getting comments:', error);
        throw error;
    }
}

/**
 * Delete a comment
 */
export async function deleteComment(postId, commentId) {
    try {
        const commentRef = doc(db, 'posts', postId, 'comments', commentId);
        await deleteDoc(commentRef);

        // Decrement comments count
        const postRef = doc(db, 'posts', postId);
        const postSnap = await getDoc(postRef);
        if (postSnap.exists()) {
            await updateDoc(postRef, {
                commentsCount: Math.max(0, (postSnap.data().commentsCount || 0) - 1),
            });
        }

        console.log('✅ Comment deleted:', commentId);
    } catch (error) {
        console.error('❌ Error deleting comment:', error);
        throw error;
    }
}

/**
 * Toggle reaction on a post
 */
export async function toggleReaction(postId, userId, reaction = 'like') {
    try {
        const reactionRef = doc(db, 'posts', postId, 'reactions', userId);
        const reactionSnap = await getDoc(reactionRef);

        if (reactionSnap.exists()) {
            // Remove reaction
            await deleteDoc(reactionRef);
            console.log('✅ Reaction removed');

            // Decrement likes count
            const postRef = doc(db, 'posts', postId);
            const postSnap = await getDoc(postRef);
            if (postSnap.exists()) {
                await updateDoc(postRef, {
                    likesCount: Math.max(0, (postSnap.data().likesCount || 0) - 1),
                });
            }

            return false; // Not liked
        } else {
            // Add reaction (use dynamic import to ensure setDoc is available at runtime)
            const firestoreMod = await import('firebase/firestore');
            await firestoreMod.setDoc(reactionRef, {
                reaction,
                createdAt: serverTimestamp(),
            });
            console.log('✅ Reaction added');

            // Increment likes count
            const postRef = doc(db, 'posts', postId);
            const postSnap = await getDoc(postRef);
            if (postSnap.exists()) {
                await updateDoc(postRef, {
                    likesCount: (postSnap.data().likesCount || 0) + 1,
                });
            }

            return true; // Liked
        }
    } catch (error) {
        console.error('❌ Error toggling reaction:', error);
        throw error;
    }
}

/**
 * Update user profile
 */
export async function updateUserProfile(userId, data) {
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            ...data,
            updatedAt: serverTimestamp()
        });

        // Also update Firebase Auth profile if it's the current user
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.uid === userId) {
            const authData = {};
            if (data.displayName) authData.displayName = data.displayName;
            if (data.photoURL) authData.photoURL = data.photoURL;
            
            if (Object.keys(authData).length > 0) {
                await updateProfile(currentUser, authData);
            }
        }

        console.log('✅ User profile updated:', userId);
    } catch (error) {
        console.error('❌ Error updating user profile:', error);
        throw error;
    }
}

/**
 * Upload group icon to Firebase Storage and update group document
 */
export async function uploadGroupIcon(groupId, file) {
    try {
        const fileRef = ref(storage, `groups/${groupId}/icon`);
        await uploadBytes(fileRef, file);
        const downloadURL = await getDownloadURL(fileRef);
        const groupRef = doc(db, 'groups', groupId);
        await updateDoc(groupRef, { iconURL: downloadURL });
        return downloadURL;
    } catch (error) {
        console.error('❌ Error uploading group icon:', error);
        throw error;
    }
}

/**
 * Upload profile picture to Firebase Storage
 */
export async function uploadProfilePicture(userId, file) {
    try {
        // Use a generic name 'avatar' to replace existing one
        const fileRef = ref(storage, `users/${userId}/profile/avatar`);
        await uploadBytes(fileRef, file);
        const downloadURL = await getDownloadURL(fileRef);
        return downloadURL;
    } catch (error) {
        console.error('❌ Error uploading profile picture:', error);
        throw error;
    }
}

/**
 * Save preferred language for a user
 * If userId is not provided, attempts to get current user from `auth.js` dynamically.
 */
export async function saveUserLanguage(lang, userId = null) {
    try {
        if (!userId) {
            const authMod = await import('./auth.js');
            const user = authMod.getCurrentUser();
            if (!user) throw new Error('User not authenticated');
            userId = user.uid;
        }

        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, { language: lang });
        console.log('✅ User language saved:', lang);
    } catch (error) {
        console.error('❌ Error saving user language:', error);
        throw error;
    }
}

/**
 * Get preferred language for a user
 * If userId is not provided, attempts to get current user from `auth.js` dynamically.
 */
export async function getUserLanguage(userId = null) {
    try {
        if (!userId) {
            const authMod = await import('./auth.js');
            const user = authMod.getCurrentUser();
            if (!user) return null;
            userId = user.uid;
        }

        const userRef = doc(db, 'users', userId);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
            const data = snap.data();
            return data.language || null;
        }
        return null;
    } catch (error) {
        console.error('❌ Error getting user language:', error);
        return null;
    }
}

/**
 * Check if user has reacted to a post
 */
export async function hasUserReacted(postId, userId) {
    try {
        const reactionRef = doc(db, 'posts', postId, 'reactions', userId);
        const reactionSnap = await getDoc(reactionRef);
        return reactionSnap.exists();
    } catch (error) {
        console.error('❌ Error checking reaction:', error);
        return false;
    }
}

// ─────────────────────────────────────────
// GROUPS
// ─────────────────────────────────────────

/**
 * Generate a random 6-character invite code
 */
function generateInviteCode() {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
}

/**
 * Create a new group and add creator as admin member
 */
export async function createGroup(data, userId) {
    try {
        const group = {
            name: data.name,
            description: data.description || '',
            emoji: data.emoji || '📖',
            createdBy: userId,
            visibility: data.visibility || 'public',
            inviteCode: data.visibility === 'private' ? generateInviteCode() : null,
            membersCount: 1,
            createdAt: serverTimestamp(),
        };

        const groupRef = await addDoc(collection(db, 'groups'), group);

        // Add creator as admin member
        await setDoc(doc(db, 'groups', groupRef.id, 'members', userId), {
            role: 'admin',
            joinedAt: serverTimestamp(),
        });

        console.log('✅ Group created:', groupRef.id);
        return groupRef.id;
    } catch (error) {
        console.error('❌ Error creating group:', error);
        throw error;
    }
}

/**
 * Get all public groups
 */
export async function getPublicGroups() {
    try {
        const q = query(
            collection(db, 'groups'),
            where('visibility', '==', 'public'),
            orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
        console.error('❌ Error getting public groups:', error);
        throw error;
    }
}

/**
 * Get all groups (public + private header for discovery)
 */
export async function getAllGroups() {
    try {
        const q = query(collection(db, 'groups'), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
        console.error('❌ Error getting all groups:', error);
        throw error;
    }
}

/**
 * Get groups where user is a member
 */
export async function getUserGroups(userId) {
    try {
        // Get all member docs for this user across groups
        const snap = await getDocs(collection(db, 'groups'));
        const groups = [];
        for (const docSnap of snap.docs) {
            const memberRef = doc(db, 'groups', docSnap.id, 'members', userId);
            const memberSnap = await getDoc(memberRef);
            if (memberSnap.exists()) {
                groups.push({ id: docSnap.id, ...docSnap.data(), memberRole: memberSnap.data().role });
            }
        }
        return groups;
    } catch (error) {
        console.error('❌ Error getting user groups:', error);
        throw error;
    }
}

/**
 * Check if user is a member of a group
 */
export async function isGroupMember(groupId, userId) {
    try {
        const memberRef = doc(db, 'groups', groupId, 'members', userId);
        const snap = await getDoc(memberRef);
        return snap.exists();
    } catch (error) {
        return false;
    }
}

/**
 * Join a group
 */
export async function joinGroup(groupId, userId) {
    try {
        await setDoc(doc(db, 'groups', groupId, 'members', userId), {
            role: 'member',
            joinedAt: serverTimestamp(),
        });
        // Increment membersCount
        await updateDoc(doc(db, 'groups', groupId), { membersCount: increment(1) });
        console.log('✅ Joined group:', groupId);
    } catch (error) {
        console.error('❌ Error joining group:', error);
        throw error;
    }
}

/**
 * Leave a group
 */
export async function leaveGroup(groupId, userId) {
    try {
        await deleteDoc(doc(db, 'groups', groupId, 'members', userId));
        await updateDoc(doc(db, 'groups', groupId), { membersCount: increment(-1) });
        console.log('✅ Left group:', groupId);
    } catch (error) {
        console.error('❌ Error leaving group:', error);
        throw error;
    }
}

/**
 * Join a private group with an invite code
 */
export async function joinGroupWithCode(inviteCode, userId) {
    try {
        const q = query(collection(db, 'groups'), where('inviteCode', '==', inviteCode.toUpperCase()));
        const snap = await getDocs(q);
        if (snap.empty) throw new Error('Code d\'invitation invalide.');
        const groupDoc = snap.docs[0];
        const alreadyMember = await isGroupMember(groupDoc.id, userId);
        if (alreadyMember) throw new Error('Vous êtes déjà membre de ce groupe.');
        await joinGroup(groupDoc.id, userId);
        return { id: groupDoc.id, ...groupDoc.data() };
    } catch (error) {
        console.error('❌ Error joining group with code:', error);
        throw error;
    }
}

/**
 * Create a post in a group
 */
export async function createGroupPost(groupId, postData, authorId) {
    try {
        const post = {
            ...postData,
            authorId,
            groupId,
            createdAt: serverTimestamp(),
            likesCount: 0,
            commentsCount: 0,
            reactions: { heart: [], amen: [], thoughtful: [], encouraging: [], inspiring: [] },
        };
        const ref = await addDoc(collection(db, 'groups', groupId, 'posts'), post);
        console.log('✅ Group post created:', ref.id);
        return ref.id;
    } catch (error) {
        console.error('❌ Error creating group post:', error);
        throw error;
    }
}

/**
 * Delete a group post
 */
export async function deleteGroupPost(groupId, postId) {
    try {
        await deleteDoc(doc(db, 'groups', groupId, 'posts', postId));
        console.log('✅ Group post deleted:', postId);
    } catch (error) {
        console.error('❌ Error deleting group post:', error);
        throw error;
    }
}
